// @ts-strict-ignore
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import { Collection, CollectionItem, Screen } from 'mytours-read-common'
import { useMediaQuery } from 'react-responsive'
import classNames from 'classnames'
import { Location } from 'history'
import { IonHeader, IonRow } from '@ionic/react'
import { ScrollCustomEvent } from '@ionic/core'
import { getDirMode } from '../../../platform'
import { getTabsUrl, getSettingsUrl } from '../../../navigation/routes'
import { CollectionsMediaItem } from '../../types'
import { getDemoModeState } from '../../functions'
import useDimensions from '../../hooks/dimensions'
import strings from '../../../../strings'
import BackButton from './BackButton'
import Button from '../../../common/components/Button'
import MapButton from './MapButton'
import ShareButton from './ShareButton'
import { ToggleMenu } from './ToggleMenu'
import HeaderPlaceholder from '../placeholders/HeaderPlaceholder'
import './styles.less'
import { HeaderFavouriteButton } from '../favourite/HeaderFavouriteButton'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import { addToFavouritedKeys, collectionItemToFavouritedItem, collectionToFavouritedItem, removeFromFavouritedKeys, screenToFavouritedItem } from 'mobile_web/modules/favourites/queries'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { FavouritedItem } from 'mobile_web/modules/favourites/types'

interface HeaderImage {
	imageData: CollectionsMediaItem
	imageUrl: string
	imageSrcSet: string
}

export interface Props {
	title?: string
	menuTitle?: string
	logoImage?: HeaderImage | null
	coverImage?: HeaderImage | null
	showBackButton?: boolean
	hideBackButton?: boolean
	showTourExitButton?: boolean
	onBackButton?: (params?: unknown) => void
	renderMenuContent?: () => JSX.Element
	getToggleMenuRef?: (ref: HTMLIonButtonElement) => void
	type?: 'none' | 'short' | 'tall' | 'image' | 'menu' | 'image_and_title' | 'hidden-menu'
	languageDir?: string
	mappable?: boolean
	onMapToggle?: () => void
	hideTitle?: boolean
	withBgColor?: boolean
	defaultScrolled?: boolean
	className?: string
	isTransparentHeader?: boolean
	loading?: boolean
	pageType?: 'list' | 'menu'
	buttonTextShadow?: boolean
	showSettingsButton?: boolean
	hideShareButton?: boolean
	backButtonLabel?: string
	indentTitle?: boolean

	hideFavouriteButton: boolean
	itemId: number
	itemType: GetCollectionItemsIndexResponseData.ItemTypeEnum
	collectionItem: CollectionItem | null
	screen: Screen | null
	collection: Collection | null
}

const Header: React.FC<Props & RouteComponentProps> = ({
	collection,
	title,
	coverImage,
	logoImage,
	type,
	renderMenuContent,
	showBackButton,
	hideBackButton,
	showTourExitButton,
	onBackButton,
	mappable,
	onMapToggle,
	hideTitle = false,
	getToggleMenuRef,
	withBgColor = true,
	defaultScrolled = false,
	className,
	isTransparentHeader = false,
	loading = false,
	menuTitle,
	match,
	location,
	pageType,
	buttonTextShadow = true,
	showSettingsButton,
	hideShareButton = false,
	backButtonLabel,
	indentTitle = false,
	hideFavouriteButton,
	itemId,
	itemType,
	collectionItem,
	screen,
}) => {
	if (type === 'none') {
		return null
	}

	if (!type) {
		if (title) {
			type = 'tall'
		} else {
			type = 'short'
		}
	}

	if ((type === 'image' || type === 'image_and_title') && !coverImage?.imageUrl) {
		type = 'tall'
	}
	const [focalPoint, setFocalPoint] = useState<number[]>([])

	const headerRef = useRef<HTMLIonHeaderElement>(null)
	const fixedRef = useRef<HTMLIonRowElement>(null)

	const fixedHeight = useDimensions(fixedRef.current).height

	const { state } = location as Location<{ prevPagePath: string }>

	const prevPagePath = state?.prevPagePath || getTabsUrl()

	const isMobile = useMediaQuery({ query: '(max-width: 575px)' })

	const isDemoMode = useMemo(() => getDemoModeState(), [])

	const ionContent = useMemo(() => {
		const firstParentElement = headerRef.current?.parentElement

		if (firstParentElement?.classList?.contains('page-content')) {
			return headerRef.current.parentElement.querySelector('.container') as HTMLIonContentElement
		}

		if (firstParentElement?.parentElement?.classList?.contains('page-content')) {
			// use double parentElement because of #container block
			return headerRef.current.parentElement.parentElement.querySelector('.container') as HTMLIonContentElement
		}

		if (firstParentElement?.parentElement?.parentElement?.classList?.contains('page-content')) {
			// use triple parentElement because of #container block
			return headerRef.current.parentElement.parentElement.parentElement.querySelector('.container') as HTMLIonContentElement
		}
	}, [headerRef, headerRef.current])

	useEffect(() => {
		if (!ionContent) {
			return
		}

		const { scrollEvents } = ionContent
		ionContent.scrollEvents = true

		const onScroll = (event: ScrollCustomEvent) => {
			const minValue = isTransparentHeader ? window.innerHeight / 3 : 0 // use default value 1/3 of height for tour start page
			const nowScrolled =
				event.target.scrollTop >
				(headerRef.current?.clientHeight || 0) - (fixedRef.current?.clientHeight || 0) + minValue

			const headerScrolled = fixedRef?.current?.classList?.contains('scrolled')

			if (nowScrolled && !headerScrolled) {
				fixedRef?.current?.classList?.add('scrolled')
			} else if (!nowScrolled && headerScrolled) {
				fixedRef?.current?.classList?.remove('scrolled')
			}
		}

		ionContent?.addEventListener('scroll', onScroll)

		return () => {
			if (ionContent) {
				ionContent.scrollEvents = scrollEvents
				ionContent?.removeEventListener('scroll', onScroll)
			}
		}
	}, [ionContent, headerRef, fixedRef])

	useEffect(() => {
		if (coverImage) {
			setFocalPoint(coverImage?.imageData?.file?.focal_point)
		}
	}, [coverImage])

	// check if the router has any params exept 'tabIndex' and 'locale'
	// it means base route and we don't need to show back button
	const showBackButtonCondition =
		showBackButton ||
		(!hideBackButton &&
			useMemo(
				() =>
					Object.entries(match.params || {}).filter(
						([key, value]) => key !== 'tabIndex' && key !== 'locale' && Boolean(value),
					).length > 0,
				[match.params],
			)) ||
		(type === 'menu' && showTourExitButton)

	const backButtonTitle = type === 'menu' ? strings.tour_exit : backButtonLabel ? backButtonLabel : null

	const hasFixedNav = showBackButtonCondition || mappable || (type === 'short' && title) || !hideShareButton
	const onlyImage = type === 'image'
	const withImage = coverImage?.imageUrl && (type === 'image_and_title' || onlyImage)

	const titleNode = useMemo(
		() => <h1 className={classNames('title', { hide: hideTitle || onlyImage })}>{title}</h1>,
		[title, hideTitle, onlyImage],
	)

	const logoNode = useMemo(
		() => logoImage && withImage && <img className="logo-image" alt={title} src={logoImage.imageUrl || ''} srcSet={logoImage.imageSrcSet || ''} />,
		[logoImage, type],
	)

	const mapButton = useMemo(
		() => (mappable ? <MapButton onClick={onMapToggle} className="map-button" /> : null),
		[mappable, onMapToggle],
	)

	const showMenu = ['menu', 'hidden-menu'].includes(type)

	const menuButton = useMemo<React.ReactElement | null>(
		() =>
			showMenu ? (
				<ToggleMenu
					menuToggleRef={getToggleMenuRef}
					title={menuTitle}
					content={renderMenuContent?.()}
					collection={collection}
				/>
			) : null,
		[type, renderMenuContent?.()],
	)

	const withButtons = useMemo<boolean>(
		() => !hideShareButton || Boolean(mapButton) || Boolean(menuButton),
		[hideShareButton, mapButton, menuButton],
	)

	const backgroundPosition =
		focalPoint &&
		(focalPoint[0] !== 0.5 || focalPoint[1] !== 0.5) &&
		focalPoint[0] &&
		focalPoint[1] &&
		`${focalPoint[0] * 100}% ${100 - focalPoint[1] * 100}%`

	const imageSrc = coverImage?.imageUrl

	const dexieFavouritesKeys = useLiveQuery(() => db.favouritedKeys.toArray())
	const favouritesKeys = dexieFavouritesKeys ? dexieFavouritesKeys.map((key) => key.key) : []
	const key = `${itemId}-${itemType}`
	const isFavourited = favouritesKeys.includes(key)

	const toggleFavorite: React.MouseEventHandler<HTMLDivElement> = (e) => {
		e.stopPropagation()
		let item: FavouritedItem | null = null
		if (collectionItem) {
			item = collectionItemToFavouritedItem(collectionItem!)
		}
		else if (collection) {
			item = collectionToFavouritedItem(collection!)

		}
		else if (screen) {
			item = screenToFavouritedItem(screen!)
		}

		if (item) {
			if (!isFavourited) {
				addToFavouritedKeys(key, item)
			}
			else {
				removeFromFavouritedKeys(key)
			}
		}
		else {
			alert('Error: no collection, collectionItem or screen to favourite')
		}
	}

	return loading ? (
		<HeaderPlaceholder />
	) : (
		<>
			<IonHeader
				className={classNames(
					'header',
					type,
					className,
					{
						'with-image': withImage,
						'with-fixed': hasFixedNav,
						'with-bg-color': withBgColor,
						'hide-title': hideTitle,
						'with-transparent': isTransparentHeader,
						'with-buttons': withButtons,
						'no-shadow': onlyImage,
						'with-demo-label': isDemoMode,
						'indented-title': indentTitle,
					},
					pageType && `page-type-${pageType}`,
				)}
				style={{
					backgroundImage: withImage && imageSrc ? `url(${imageSrc})` : undefined,
					paddingTop: (!isTransparentHeader && fixedHeight && !withButtons) || undefined,
					backgroundPosition: backgroundPosition || 'center',
				}}
				ref={headerRef}
				tabIndex={type === 'hidden-menu' ? 0 : 1}
				dir={getDirMode()}
			>
				{isDemoMode && (
					<IonRow className="demo-mode-label">
						<h4>{strings.demo_mode_label}</h4>
					</IonRow>
				)}
				<IonRow
					className={classNames('fixed-nav', {
						empty: !hasFixedNav,
						'default-scrolled': defaultScrolled,
						'no-button-text-shadow': !buttonTextShadow,
					})}
					ref={fixedRef}
				>
					<div className="nav-block side left">
						{showBackButtonCondition ? (
							<BackButton
								prevPagePath={prevPagePath}
								onBackButton={onBackButton}
								className={classNames('back-button', {
									'with-icon': type !== 'menu',
								})}
								icon={showMenu ? null : undefined}
								text={backButtonTitle || ''}
							/>
						) : null}
					</div>
					<div className="nav-block center title-container">{titleNode}</div>
					<div className="nav-block side right">
						{!hideShareButton && <ShareButton />}
						{!hideFavouriteButton ? <HeaderFavouriteButton
							onClick={toggleFavorite}
							isFavourited={isFavourited}
						/> : null}
						{mapButton}
						{menuButton}
					</div>
				</IonRow>
				{showSettingsButton ? (
					<IonRow className="header-row">
						{titleNode}
						<Button fill="clear" ariaLabel={strings.settings} href={getSettingsUrl(isDemoMode)} className="header-cog-button">
							<div className="header-cog-button-icon-container" >
								<i className="fal fa-gear" />
							</div>
						</Button>
					</IonRow>
				) : (
					logoNode || titleNode
				)}
			</IonHeader>
		</>
	)
}

export default withRouter(Header)
